import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { CopyIcon, EyeIcon, Trash2Icon } from "lucide-react";

import { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { Container } from "simplydo/react/ui/container";
import { H3, H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Message, MessageHeader } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Popup } from "simplydo/react/ui/popup";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import util from "utils/utils";

type ApiToken =
  OpenAPI.GET<"/accounts/api/tokens">["response"]["externalTokens"][number];

type ApiTokenProps = {
  token: ApiToken;
  tokenIndex: number;
  deleteToken: () => void;
  onViewToken: (tokenId: string | undefined, tokenIndex: number) => void;
};

const ApiToken = ({
  token,
  tokenIndex,
  deleteToken,
  onViewToken,
}: ApiTokenProps) => (
  <TableRow>
    <TableCell collapsing>{token.serviceName || "Unknown"}</TableCell>
    <TableCell>
      <Popup
        trigger={
          <Input
            fluid
            value={"x".repeat(20)}
            size="small"
            readOnly
            type="password"
          />
        }
      >
        {token.userHasViewedToken
          ? "You have already viewed this token. It is no longer visible for security reasons. If you have the lost the token information you will need to delete it and recreate a token for the relevant service"
          : "You are able to view this token once. Press the 'View token' button to view the token. It will no longer be visible after this."}
      </Popup>
    </TableCell>
    <TableCell collapsing>
      {!token.userHasViewedToken ? (
        <Button
          size="small"
          onClick={() => onViewToken(token.id, tokenIndex)}
          color="primary"
        >
          <Icon icon={EyeIcon} />
          View token
        </Button>
      ) : null}
      <Button size="small" onClick={() => deleteToken()} variant="outline">
        <Icon icon={Trash2Icon} />
      </Button>
    </TableCell>
  </TableRow>
);

function ApiSettings() {
  const { t } = useTranslation();

  const [newApiTokenService, setNewApiTokenService] = useState("");
  const [creatingToken, setCreatingToken] = useState(false);
  const [externalTokens, setExternalTokens] = useState<ApiToken[]>([]);
  const [viewingExternalToken, setViewingExternalToken] =
    useState<ApiToken | null>(null);
  const copyToken = () => {
    void util.copyToClipboard(viewingExternalToken?.token || "");
  };

  useEffect(() => {
    api.auth.getApiTokens(
      (newTokens) => {
        setExternalTokens(newTokens.externalTokens);
      },
      () => {},
    );
  }, []);

  const createToken = () => {
    setCreatingToken(true);
    api.auth.createExternalApiToken(
      { serviceName: newApiTokenService },
      (token) => {
        setCreatingToken(false);
        setExternalTokens([...externalTokens, token]);
        setViewingExternalToken(token);
        setNewApiTokenService("");
        toast("Your new token is ready to be used");
      },
      (err) => {
        toast.error(err.message);
        setCreatingToken(false);
      },
    );
  };

  const viewToken = useCallback(
    (tokenId: string | undefined, tokenIndex: number) => {
      util
        .confirm(
          "View API token",
          "This will mark the token as viewed, and it will no longer be visible to you. Please note down the token to continue using it. Are you sure you want to continue?",
        )
        .then(() => {
          api.auth.viewExternalApiToken(
            tokenId,
            tokenIndex,
            ({ success, token: viewingToken }) => {
              setExternalTokens((prevTokens) =>
                prevTokens.map((token, index) => {
                  if (index === tokenIndex) {
                    return { ...token, userHasViewedToken: true };
                  }
                  return token;
                }),
              );
              if (!success) {
                toast.error("You have already viewed this token");
              } else {
                if (!viewingToken) {
                  toast.error("Could not show token");
                  return;
                }
                setViewingExternalToken(viewingToken);
              }
            },
            (err) => {
              toast.error(err.message);
            },
          );
        })
        .catch(() => {});
    },
    [],
  );

  const deleteToken = (tokenId: string | undefined, tokenIndex: number) => {
    util
      .confirm(
        "Delete this token?",
        `Any services using this token will no longer be able to access your ${util.appName()} account.`,
      )
      .then(() => {
        setCreatingToken(true);
        api.auth.deleteExternalApiToken(
          tokenId,
          tokenIndex,
          () => {
            setCreatingToken(false);
            if (tokenId) {
              setExternalTokens(
                externalTokens.filter((token) => tokenId !== token.id),
              );
            } else {
              setExternalTokens(
                externalTokens.filter((_, index) => index !== tokenIndex),
              );
            }
          },
          (err) => {
            setCreatingToken(false);
            toast.error(err.message);
          },
        );
      })
      .catch(() => {});
  };

  return (
    <Container>
      <H3>
        {t("users.account.options.apiTokens.title")}

        <Subheader>
          {t("users.account.options.apiTokens.info", {
            appName: util.appName(),
          })}
        </Subheader>
      </H3>

      <H5>
        {t("users.account.options.apiTokens.new")}
        <Subheader>{t("users.account.options.apiTokens.newInfo")}</Subheader>
      </H5>
      <Input
        fluid
        value={newApiTokenService}
        action={
          <Button onClick={createToken} color="primary">
            Create
          </Button>
        }
        loading={creatingToken}
        placeholder='Service name (e.g. "Zapier")'
        onChange={(e) => setNewApiTokenService(e.target.value)}
      />

      <H5>
        Tokens you&apos;ve already created
        <Subheader>
          To authenticate using these tokens, call the appropriate endpoint over{" "}
          <code>HTTPS</code>, placing the token in the{" "}
          <code>Authorization</code> header. For more information about using
          the {util.appName()} API please get in touch.
        </Subheader>
      </H5>
      {externalTokens.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Service</TableHeaderCell>
              <TableHeaderCell>Token</TableHeaderCell>
              <TableHeaderCell />
            </TableRow>
          </TableHeader>
          <TableBody>
            {externalTokens.map((token, tokenIndex) => (
              <ApiToken
                key={token.id}
                token={token}
                deleteToken={() => deleteToken(token.id, tokenIndex)}
                tokenIndex={tokenIndex}
                onViewToken={viewToken}
              />
            ))}
          </TableBody>
        </Table>
      ) : (
        <Message>No tokens have been created yet.</Message>
      )}

      <Modal
        open={!!viewingExternalToken}
        onClose={() => setViewingExternalToken(null)}
        closeOnDimmerClick={false}
      >
        <ModalHeader>Viewing external API token</ModalHeader>
        <ModalContent>
          <Message color="orange">
            <MessageHeader>Important!</MessageHeader>
            <p>
              This token is only shown once. Please copy it now and keep it
              safe. Once you close this modal, you cannot view this token again.
            </p>
          </Message>
          <Input
            fluid
            value={viewingExternalToken?.token}
            size="small"
            readOnly
            action={
              <Button onClick={copyToken}>
                <Icon icon={CopyIcon} />
              </Button>
            }
          />
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setViewingExternalToken(null)} color="primary">
            Done
          </Button>
        </ModalActions>
      </Modal>
    </Container>
  );
}

export default ApiSettings;
